import { GraphQLService } from '@pique/core/modules/cloud';
import type {
  GraphQLQuery,
  MutationOptions,
  QueryOptions,
  QueryResponse,
  QueryVariables,
  RecursiveMaybeFields,
} from '@pique/graphql';
import { useServices } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import { useMemo } from 'react';
import { useSWRConfig } from 'swr';
import type {
  SWRMutationConfiguration,
  SWRMutationResponse,
} from 'swr/mutation';
import useSWRMutation from 'swr/mutation';

type MutationKey = () => readonly [string, string];

/**
 * A useSWRMutation wrapper for sending graphql mutations
 *
 * @example
 *
 * ```ts
 * import { someMutation } from '@pique/graphql'
 *
 * const { trigger } = useMutation({
 *  mutation: someMutation,
 * })
 *
 * trigger({ name: 'John Doe' })
 */
export function useMutation<Mutation extends GraphQLQuery>(
  options: Omit<MutationOptions<Mutation>, 'variables'>,
  config?: Omit<
    SWRMutationConfiguration<
      QueryResponse<Mutation>,
      GraphQLError,
      MutationKey,
      QueryVariables<Mutation>
    >,
    'fetcher'
  >
): SWRMutationResponse<
  QueryResponse<Mutation>,
  GraphQLError,
  MutationKey,
  QueryVariables<Mutation>
> {
  const { graphQLService } = useServices({ GraphQLService });
  const fetcher = (
    _: readonly [string, string],
    { arg }: { arg: QueryVariables<Mutation> }
  ) =>
    graphQLService.gql({
      query: options['mutation'],
      variables: arg,
      context: options['context'],
    } as unknown as QueryOptions<Mutation>);

  return useSWRMutation<
    QueryResponse<Mutation>,
    GraphQLError,
    MutationKey,
    QueryVariables<Mutation>
  >(
    () => ['cloud', options['mutation'].id] as const,
    fetcher,
    config
  );
}

// use this to revalidate all queries that match the filter
export const useMutateQueryResource = () => {
  const { mutate } = useSWRConfig();
  const revalidateResource = useMemo(
    () =>
      <Q extends GraphQLQuery>(
        query: Q,
        varsFilter: (
          vars: RecursiveMaybeFields<QueryVariables<Q>>
        ) => boolean = _vars => true
      ) => {
        return mutate(key => {
          const res =
            Array.isArray(key) &&
            key[0] === 'cloud' &&
            key[1] === query.id &&
            varsFilter(key[2]);
          return res;
        });
      },
    [mutate]
  );

  return revalidateResource;
};
